import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  ProtectedRoute,
  PublicRoute,
  AdminRoute,
  PermissionRoute,
} from "./RouteGuard";
import { PERMISSIONS } from "../utils/permissions";

// Import components
import Dashboard from "../components/Dashboard/Dashboard";
import Login from "../components/Auth/Login";
import UserManagement from "../components/User/UserManagement";
import OnlineUsers from "../components/User/OnlineUsers";
import Profile from "../components/User/Profile";
import TokenManagement from "../components/Token/TokenManagement";
import ExchangeManagement from "../components/Exchange/ExchangeManagement";
import Reports from "../components/Reports/Reports";
import ReportDetail from "../components/Reports/ReportDetail";
import TradeDataTable from "../components/TradeData/TradeDataTable";
import TradeDataChart from "../components/TradeData/TradeDataChart";
import AccountBalanceTable from "../components/AccountBalance/AccountBalanceTable";
import AccountConfigList from "../components/AccountConfig/AccountConfigList";
import BusinessLineManagement from "../components/MasterData/BusinessLineManagement";
import StrategyTypeManagement from "../components/MasterData/StrategyTypeManagement";
import TransactionList from "../components/Transaction/TransactionList";
import ApiTestPage from "../components/Debug/ApiTestPage";
import Settings from "../components/Settings/Settings";
import ApiDocs from "../components/ApiDocs/ApiDocs";
import FundTrendsChart from "../components/AccountBalance/FundTrendsChart";
import CompositeOverview from "../components/Dashboard/CompositeOverview";
import HedgeMonitor from "../components/HedgeMonitor/HedgeMonitor";
import AlertRuleList from "../components/Alert/AlertRuleList";
import AlertList from "../components/Alert/AlertList";
import TaskList from "../components/Task/TaskList";
import ChannelList from "../components/Notification/ChannelList";
import CompareChart from "../components/Analytics/CompareChart";
import AttributionView from "../components/Analytics/AttributionView";
import ForecastChart from "../components/Analytics/ForecastChart";
import SystemHealth from "../components/Monitor/SystemHealth";
import AuditLogs from "../components/Monitor/AuditLogs";
import RavenDownload from "../components/RavenDownload/RavenDownload";

const AppRoutes = () => {
  return (
    <Routes>
      {/* 公开路由 */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* 受保护的路由 */}
      <Route
        path="/dashboard"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_DASHBOARD]}>
            <Dashboard />
          </PermissionRoute>
        }
      />

      {/* 管理员专用路由 */}
      <Route
        path="/users"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <UserManagement />
          </PermissionRoute>
        }
      />

      <Route
        path="/tokens"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TOKENS]}>
            <TokenManagement />
          </PermissionRoute>
        }
      />

      <Route
        path="/exchanges"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <ExchangeManagement />
          </PermissionRoute>
        }
      />

      <Route
        path="/online-users"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_ONLINE_USERS]}>
            <OnlineUsers />
          </PermissionRoute>
        }
      />

      {/* 普通用户和管理员都可访问的路由 */}
      <Route
        path="/trade-data"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <TradeDataTable />
          </PermissionRoute>
        }
      />

      <Route
        path="/trade-chart"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <TradeDataChart />
          </PermissionRoute>
        }
      />

      <Route
        path="/balances"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <AccountBalanceTable />
          </PermissionRoute>
        }
      />
      <Route
        path="/fund-trends"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_FUND_TRENDS]}>
            <FundTrendsChart />
          </PermissionRoute>
        }
      />

      <Route
        path="/account-configs"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_ACCOUNT_CONFIG]}>
            <AccountConfigList />
          </PermissionRoute>
        }
      />

      <Route
        path="/business-lines"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_ACCOUNT_CONFIG]}>
            <BusinessLineManagement />
          </PermissionRoute>
        }
      />

      <Route
        path="/strategy-types"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_ACCOUNT_CONFIG]}>
            <StrategyTypeManagement />
          </PermissionRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRANSACTIONS]}>
            <TransactionList />
          </PermissionRoute>
        }
      />

      <Route
        path="/composite-overview"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_COMPOSITE_OVERVIEW]}>
            <CompositeOverview />
          </PermissionRoute>
        }
      />

      <Route
        path="/hedge-monitor"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_HEDGE_GROUPS]}>
            <HedgeMonitor />
          </PermissionRoute>
        }
      />

      {/* 告警管理 - 管理员专用 */}
      <Route
        path="/alert-rules"
        element={
          <PermissionRoute permissions={[PERMISSIONS.MANAGE_ALERTS]}>
            <AlertRuleList />
          </PermissionRoute>
        }
      />

      <Route
        path="/alerts"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_ALERTS]}>
            <AlertList />
          </PermissionRoute>
        }
      />

      {/* 任务管理 - 管理员专用 */}
      <Route
        path="/tasks"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <TaskList />
          </PermissionRoute>
        }
      />

      {/* 通知渠道 - 管理员专用 */}
      <Route
        path="/notification-channels"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <ChannelList />
          </PermissionRoute>
        }
      />

      {/* 分析功能（普通用户见本人数据，管理员见全量） */}
      <Route
        path="/analytics/compare"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <CompareChart />
          </PermissionRoute>
        }
      />

      <Route
        path="/analytics/attribution"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <AttributionView />
          </PermissionRoute>
        }
      />

      <Route
        path="/analytics/forecast"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <ForecastChart />
          </PermissionRoute>
        }
      />

      {/* 系统监控 - 管理员专用 */}
      <Route
        path="/monitor/health"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <SystemHealth />
          </PermissionRoute>
        }
      />

      <Route
        path="/monitor/metrics"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <SystemHealth />
          </PermissionRoute>
        }
      />

      {/* 审计日志 - 管理员专用 */}
      <Route
        path="/audit/logs"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <AuditLogs />
          </PermissionRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_REPORTS]}>
            <Reports />
          </PermissionRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_REPORTS]}>
            <ReportDetail />
          </PermissionRoute>
        }
      />

      {/* Raven 工具包下载 */}
      <Route
        path="/raven-download"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_TRADE_DATA]}>
            <RavenDownload />
          </PermissionRoute>
        }
      />

      {/* API 文档 */}
      <Route
        path="/api-docs"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <ApiDocs />
          </PermissionRoute>
        }
      />

      {/* Debug route */}
      <Route path="/debug" element={<ApiTestPage />} />

      {/* 系统设置 - 管理员专用 */}
      <Route
        path="/settings"
        element={
          <PermissionRoute permissions={[PERMISSIONS.VIEW_USERS]}>
            <Settings />
          </PermissionRoute>
        }
      />

      {/* 个人中心 - 所有登录用户可访问 */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute fallbackPath="/login">
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* 默认路由 */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 路由 */}
      <Route
        path="*"
        element={
          <ProtectedRoute fallbackPath="/login">
            <div className="page-not-found">页面未找到</div>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
